import type { FC } from 'react';
import {
  DevHubDialog,
  DevHubDialogTitle,
  DevHubDialogContent,
  DevHubDialogActions,
  DevHubButton,
  DevHubTypography,
  DevHubLink,
} from '@devxdiscover/devhub-ui';
import type { OwnerEligibilityTier } from '../../util/ownerValidation';

const DEVHUB_SLACK_URL = 'https://cim.slack.com/archives/C02N9B1E4QH';

export interface CGAOwnerLevelDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Display name of the selected user (e.g., "Jane Doe"). */
  userName: string;
  /** Job title of the selected user (e.g., "SVP Engineering"). */
  userTitle: string;
  /** Eligibility tier — controls the message variant and available actions. */
  tier: OwnerEligibilityTier;
  /** Called when the dialog is dismissed (OK or Cancel). */
  onClose: () => void;
  /** Called when the warn-tier user clicks "Generate Anyway". */
  onConfirm: () => void;
}

/**
 * CGA-specific dialog shown when a selected report target falls within a
 * restricted employee level tier. The exclude tier blocks report generation;
 * the warn tier asks for confirmation.
 *
 * This is the CGA counterpart to OwnerLevelDialog — it uses
 * report-generation verbiage instead of owner-assignment verbiage.
 *
 * @param props - {@link CGAOwnerLevelDialogProps}
 */
export const CGAOwnerLevelDialog: FC<CGAOwnerLevelDialogProps> = ({
  open,
  userName,
  userTitle,
  tier,
  onClose,
  onConfirm,
}) => {
  if (tier === 'allow') {
    return null;
  }

  const nameLabel = userTitle ? `${userName} (${userTitle})` : userName;

  const isExclude = tier === 'exclude';

  const title = isExclude
    ? 'User Selection Restricted'
    : 'Confirm Report Generation';

  const message = isExclude
    ? `${nameLabel} may hold a senior leadership position. Generating` +
      ' CGA hierarchy reports for senior executives requires' +
      ' additional authorization.'
    : `Are you sure? ${nameLabel} holds a senior leadership position.` +
      ' Hierarchy reports at this level typically include a large number of direct and indirect reports, which may return a significantly higher number of records.';

  return (
    <DevHubDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DevHubDialogTitle>{title}</DevHubDialogTitle>

      <DevHubDialogContent>
        <DevHubTypography variant="body1" style={{ marginBottom: '1rem' }}>
          {message}
        </DevHubTypography>

        <DevHubTypography variant="body2">
          For questions, contact{' '}
          <DevHubLink href={DEVHUB_SLACK_URL} target="_blank" rel="noopener">
            #devhub
          </DevHubLink>{' '}
          on Slack.
        </DevHubTypography>
      </DevHubDialogContent>

      <DevHubDialogActions>
        {isExclude ? (
          <DevHubButton onClick={onClose} variant="contained" color="primary">
            OK
          </DevHubButton>
        ) : (
          <>
            <DevHubButton onClick={onClose} variant="outlined">
              Cancel
            </DevHubButton>
            <DevHubButton
              onClick={onConfirm}
              variant="contained"
              color="primary"
            >
              Generate Anyway
            </DevHubButton>
          </>
        )}
      </DevHubDialogActions>
    </DevHubDialog>
  );
};
